import axios from 'axios';
import { getIdentityToken } from '@/services/http';
import List from './list';

// Session structure for API responses
export interface Session {
  session_id: string;
  title: string;
  agent_name?: string;
  agent_id?: string;
  created_at: string;
  updated_at: string;
  message_count?: number;
}

// API error response structure
export interface SessionApiError {
  message: string;
  code?: string;
  details?: any;
}

export default class SessionsCollection extends List<Session> {

  /**
   * Delete a specific chat session using the Atlas backend API
   *
   * @param sessionId - The session ID to delete
   * @returns Promise<boolean> - Success status
   */
  public async deleteSession(sessionId: string): Promise<boolean> {
    try {
      const token = this.getAuthToken();
      const baseURL = this.getBaseURL();
      const fullURL = `${baseURL}/sessions/${sessionId}`;

      // Validate sessionId parameter
      if (!sessionId || sessionId.trim() === '') {
        throw new Error('Session ID is required for deletion');
      }

      // Debug logging

      // Send DELETE request to session endpoint
      await axios.delete(fullURL, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        timeout: 10000, // 10 second timeout for deletion
      });


      // Remove from local collection if it exists
      this.items = this.items.filter((session) => session.session_id !== sessionId);

      return true;
    } catch (error: any) {
      console.error('❌ SessionsCollection: Error deleting session:', error);
      console.error('  Session ID:', sessionId);
      console.error('  Request URL:', error.config?.url);
      console.error('  Request Headers:', error.config?.headers);
      console.error('  Response Status:', error.response?.status);
      console.error('  Response Data:', error.response?.data);

      // Provide detailed error information for different error types
      if (error.response) {
        const status = error.response.status;
        const errorData = error.response.data as SessionApiError;
        const message = errorData?.message || error.response.statusText || 'Unknown API error';

        // Handle specific HTTP status codes with user-friendly messages
        switch (status) {
          case 404:
            throw new Error(`Session not found: The session "${sessionId}" does not exist or has already been deleted.`);
          case 403:
            throw new Error('Permission denied: You do not have permission to delete this session.');
          case 401:
            throw new Error('Authentication required: Please log in to delete sessions.');
          case 500:
            throw new Error('Server error: Unable to delete session due to a server problem. Please try again later.');
          default:
            throw new Error(`Delete Session Error: ${status} - ${message}`);
        }
      } else if (error.request) {
        throw new Error('Network Error: Unable to reach the Atlas API. Please check your connection and try again.');
      } else {
        // Handle validation errors or other client-side errors
        throw new Error(`Request Error: ${error.message}`);
      }
    }
  }

  /**
   * Get a session by ID from the local collection
   *
   * @param sessionId - The session ID to find
   * @returns Session | undefined - The found session or undefined
   */
  public getSessionById(sessionId: string): Session | undefined {
    return this.items.find((session) => session.session_id === sessionId);
  }

  /**
   * Remove a session from the local collection (without API call)
   *
   * @param sessionId - The session ID to remove locally
   * @returns boolean - True if session was found and removed, false otherwise
   */
  public removeSessionLocally(sessionId: string): boolean {
    const initialSize = this.items.length;
    this.items = this.items.filter((session) => session.session_id !== sessionId);
    return this.items.length < initialSize;
  }

  /**
   * Add or update a session in the collection
   *
   * @param session - The session to add or update
   */
  public addOrUpdateSession(session: Session): void {
    const existingIndex = this.items.findIndex((s) => s.session_id === session.session_id);
    if (existingIndex !== -1) {
      this.items[existingIndex] = session;
    } else {
      this.add(session);
    }
  }

  /**
   * Get authentication token from localStorage
   */
  private getAuthToken(): string {
    return getIdentityToken() || 'test-token';
  }

  /**
   * Get base URL for Atlas API
   */
  private getBaseURL(): string {
    return process.env.VUE_APP_ATLAS_API_URL || 'http://localhost:8000';
  }
}
